import { mobile, tablet } from "components/templates/Breakpoints";
import { H1 as _H1 } from "components/atoms/Heading";
import React from "react";
import { types, Image } from "react-bricks/frontend";
import styled from "styled-components";
import { backgroundColorProp } from "../utils/backgroundColor";
import { Repeater } from "react-bricks/frontend";
import { HeroLeftTextProps } from "./HeroLeftText";
import { Clouds } from "./Clouds";
import { color } from "@styles";

interface HeroFormProps {
  backgroundColor?: types.IColor;
  leftText?: types.RepeaterItems<HeroLeftTextProps>;
  showClouds?: boolean;
  cloudColor?: types.IColor;
  showYugiImage?: boolean;
  imageTop?: number;
  imageLeft?: number;
  imageRotate?: number;
  useBackgroundImage?: boolean;
  backgroundImageUrl?: types.IImageSource;
}

const HeroForm: types.Brick<HeroFormProps> = ({
  backgroundColor,
  showClouds,
  cloudColor,
  showYugiImage = false,
  imageTop = -100,
  imageLeft = -140,
  imageRotate = -25,
  useBackgroundImage,
  backgroundImageUrl,
}) => {
  return (
    <Container
      backgroundColor={backgroundColor?.color}
      backgroundImage={
        useBackgroundImage && backgroundImageUrl?.src ? backgroundImageUrl.src : undefined
      }
    >
      {showClouds && <Clouds color={cloudColor?.color} />}
      <Left>
        <Repeater propName="leftText" />
      </Left>
      <Right>
        {showYugiImage && (
          <YugiImageContainer top={imageTop} left={imageLeft} rotate={imageRotate}>
            <Image
              propName="image"
              alt="image"
              maxWidth={440}
              containerStyle={{ width: "100%", height: "auto" }}
              imageStyle={{ width: "100%", height: "auto" }}
            />
          </YugiImageContainer>
        )}
        <FormWrapper>
          <Repeater propName="form" />
        </FormWrapper>
      </Right>
    </Container>
  );
};

HeroForm.schema = {
  name: "hero-form",
  label: "Hero Landing (Form)",
  previewImageUrl: "/static/bricks/hero-form.png",
  repeaterItems: [
    {
      name: "leftText",
      itemType: "hero-left-text",
      itemLabel: "Copy",
      min: 1,
      max: 1,
    },
    {
      name: "form",
      itemType: "hubspot-form",
      itemLabel: "Form",
      min: 1,
      max: 1,
    },
  ],

  // Defaults when a new brick is added
  getDefaultProps: () => ({
    leftText: [
      {
        title: "Title to describe what the webinar is about to go here here here what",
        body: "Supporting copy here",
        topImageWidth: 170,
        showBody: true,
        showBottomImage: true,
        showTopImage: true,
      },
    ],
    form: [
      {
        redirectUrl: "/form/success",
      },
    ],
    showClouds: false,
    image: {
      src: "/static/giraffes/BalloonYugi.svg",
    },
    imageTop: -100,
    imageLeft: -140,
    imageRotate: -25,
    useBackgroundImage: false,
    backgroundImageUrl: {
      src: "/static/backgrounds/hero-bg.jpg",
    },
  }),

  // Sidebar Edit controls for props
  sideEditProps: [
    backgroundColorProp,
    {
      name: "showClouds",
      label: "Show Clouds?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "cloudColor",
      label: "Cloud Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      show: (props) => props.showClouds,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { value: { color: color.white }, label: "White" },
          { value: { color: color.S320 }, label: "Purple" },
          { value: { color: color.S220 }, label: "Blue" },
          { value: { color: color.S120 }, label: "Green" },
          { value: { color: color.S430 }, label: "Yellow" },
        ],
      },
    },
    {
      name: "useBackgroundImage",
      label: "Use Background Image?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "backgroundImageUrl",
      label: "Background Image",
      type: types.SideEditPropType.Image,
      show: (props) => props.useBackgroundImage,
    },
    {
      name: "showYugiImage",
      label: "Show Yugi Image on Form?",
      type: types.SideEditPropType.Boolean,
      shouldRefreshStyles: true,
    },
    {
      name: "imageTop",
      label: "Image Top Offset",
      shouldRefreshStyles: true,
      show: (props) => props.showYugiImage,
      type: types.SideEditPropType.Range,
      rangeOptions: {
        min: -150,
        max: 100,
        step: 5,
      },
    },
    {
      name: "imageLeft",
      label: "Image Left Offset",
      shouldRefreshStyles: true,
      show: (props) => props.showYugiImage,
      type: types.SideEditPropType.Range,
      rangeOptions: {
        min: -200,
        max: 100,
        step: 5,
      },
    },
    {
      name: "imageRotate",
      label: "Image Rotation (deg)",
      shouldRefreshStyles: true,
      show: (props) => props.showYugiImage,
      type: types.SideEditPropType.Range,
      rangeOptions: {
        min: -180,
        max: 180,
        step: 1,
      },
    },
  ],
};

export default HeroForm;

const Container = styled.div<{ backgroundColor: string; backgroundImage?: string }>`
  background: ${(p) =>
    p.backgroundImage ? `url(${p.backgroundImage}) center/cover no-repeat` : p.backgroundColor};
  max-width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding: 9rem 6rem 8rem 6rem;

  ${tablet} {
    flex-direction: column;
    padding: 4rem;
  }

  ${mobile} {
    padding: 0.5rem;
  }
`;

const Left = styled.div`
  width: 55%;
  max-width: 40rem;
  margin-right: 5rem;
  margin-top: -20rem;
  z-index: 1;
  ${tablet} {
    width: 100%;
    margin-bottom: 4rem;
    margin-right: 0rem;
    margin-top: 0rem;
    img {
      max-width: 100%;
    }
  }
`;

const Right = styled.div`
  width: 45%;
  max-width: 40rem;
  position: relative;

  ${tablet} {
    width: 100%;
  }
`;

const YugiImageContainer = styled.div<{
  top: number;
  left: number;
  rotate?: number;
}>`
  position: absolute;
  top: ${(p) => p.top}px;
  left: ${(p) => p.left}px;
  transform: rotate(${(p) => p.rotate ?? 0}deg);
  z-index: 0;

  ${tablet} {
    display: none;
  }
`;

const FormWrapper = styled.div`
  position: relative;
  z-index: 1;
`;
